import type { EditorState } from "./state";
import { activeSubjectAssetId } from "./state";
import type {
  ImageSlotDisplay,
  ImageSlotId,
  Placement,
  Rect,
  TemplateDef,
  TextSlotId,
} from "./types";

// 画面表示（編集キャンバス）と出力画像の両方を、この描画仕様から生成する。
// 表示と出力を別実装にすると乖離バグを防げないため、生成元を一本化している。

export interface RectNode {
  kind: "rect";
  rect: Rect;
  fill: string;
  cornerRadius?: number;
  opacity?: number;
}

export interface ImageNode {
  kind: "image";
  slot: ImageSlotId;
  assetId: string;
  frame: Rect;
  display: ImageSlotDisplay;
  cornerRadius?: number;
  borderColor?: string;
  borderWidth?: number;
  glowColor?: string;
  glowBlur?: number;
  // 描く画像の位置とサイズ（wipe はクリップ前提、cutout はクリップなし）
  draw: Rect;
  editable: true;
}

export interface TextNode {
  kind: "text";
  slot: TextSlotId | "decor" | "watermark";
  text: string;
  frame: Rect;
  fontFamily: string;
  fontSize: number;
  fill: string;
  stroke?: string;
  strokeWidth?: number;
  fontStyle?: string;
  align: "left" | "center" | "right";
  opacity?: number;
  editable: boolean;
}

export type RenderNode = RectNode | ImageNode | TextNode;

export interface AssetDimensions {
  width: number;
  height: number;
}

export type AssetDimensionLookup = (assetId: string) => AssetDimensions | null;

export type TextMeasure = (text: string, fontSize: number, fontFamily: string) => number;

// フォントメトリクスに依存しない近似測定（テスト・フォールバック用）。
// 全角 1em・半角 0.55em として概算する
export const approximateMeasure: TextMeasure = (text, fontSize) => {
  let units = 0;
  for (const ch of text) {
    units += ch.charCodeAt(0) <= 0x7f ? 0.55 : 1.0;
  }
  return units * fontSize;
};

export const defaultPlacement: Placement = { scale: 1, offsetX: 0, offsetY: 0 };

// スロットに描く素材の解決。ワイプ（小窓）は実際の配信のカメラ映像と同じく
// 常に元画像（背景つき）を使う。透過素材を小窓に入れると背後のゲーム画面が
// 透けて不自然になるため、切り抜き（ステップ2 の選択）の適用は立ち絵に限る
export function resolveSlotAssetId(
  template: TemplateDef,
  state: EditorState,
  slotId: ImageSlotId,
): string | null {
  if (slotId === "main") return state.main?.assetId ?? null;
  const display = template.imageSlots.facecam.display ?? "wipe";
  return display === "cutout"
    ? activeSubjectAssetId(state)
    : (state.subject?.assetId ?? null);
}

// スロット枠を覆う最小倍率（カバーフィット）を基準に、ユーザー配置
// （相対倍率・中心オフセット）を適用した描画矩形を返す
export function coverDraw(image: AssetDimensions, frame: Rect, placement: Placement): Rect {
  const base = Math.max(frame.width / image.width, frame.height / image.height);
  const scale = base * placement.scale;
  const width = image.width * scale;
  const height = image.height * scale;
  return {
    x: frame.x + (frame.width - width) / 2 + placement.offsetX,
    y: frame.y + (frame.height - height) / 2 + placement.offsetY,
    width,
    height,
  };
}

// 立ち絵（cutout）用: 枠に収まる最大倍率（コンテインフィット）を基準に、
// 水平は中央・垂直は下端基準で配置する（立ち絵は足元／胴の下端を揃えるのが自然）
export function containDraw(image: AssetDimensions, frame: Rect, placement: Placement): Rect {
  const base = Math.min(frame.width / image.width, frame.height / image.height);
  const scale = base * placement.scale;
  const width = image.width * scale;
  const height = image.height * scale;
  return {
    x: frame.x + (frame.width - width) / 2 + placement.offsetX,
    y: frame.y + frame.height - height + placement.offsetY,
    width,
    height,
  };
}

// 枠内に収まるフォントサイズを返す。上限内のはみ出しは自動縮小で吸収し、
// 枠外への見切れを起こさない（機能設計書 3.1）
export function fitFontSize(
  text: string,
  baseSize: number,
  maxWidth: number,
  fontFamily: string,
  measure: TextMeasure,
): number {
  if (text.length === 0) return baseSize;
  const width = measure(text, baseSize, fontFamily);
  if (width <= maxWidth) return baseSize;
  return Math.max(8, Math.floor((baseSize * maxWidth) / width));
}

export function formatViewers(value: number): string {
  return `視聴者 ${Math.max(0, Math.floor(value)).toLocaleString("ja-JP")}人`;
}

export function buildRenderSpec(
  template: TemplateDef,
  state: EditorState,
  lookup: AssetDimensionLookup,
  measure: TextMeasure = approximateMeasure,
): RenderNode[] {
  const nodes: RenderNode[] = [];

  nodes.push({
    kind: "rect",
    rect: { x: 0, y: 0, width: template.width, height: template.height },
    fill: template.backdropFill,
  });

  // wipe は装飾（帯等）の下、cutout（立ち絵）は装飾の上に重ねる。
  // 立ち絵がゲーム画面・情報帯の前面に立つのが配信画面の定番の重なり順のため
  const overlayNodes: ImageNode[] = [];

  for (const slotId of ["main", "facecam"] as const) {
    const def = template.imageSlots[slotId];
    const display = def.display ?? "wipe";
    const assetId = resolveSlotAssetId(template, state, slotId);
    if (assetId === null) continue;
    const dims = lookup(assetId);
    if (dims === null) continue;
    const placement = state.edits[slotId]?.placement ?? defaultPlacement;
    const node: ImageNode = {
      kind: "image",
      slot: slotId,
      assetId,
      frame: def.frame,
      display,
      cornerRadius: def.cornerRadius,
      borderColor: def.borderColor,
      borderWidth: def.borderWidth,
      glowColor: def.glowColor,
      glowBlur: def.glowBlur,
      draw:
        display === "cutout"
          ? containDraw(dims, def.frame, placement)
          : coverDraw(dims, def.frame, placement),
      editable: true,
    };
    if (display === "cutout") {
      overlayNodes.push(node);
    } else {
      nodes.push(node);
    }
  }

  for (const decor of template.decors) {
    if (decor.kind === "rect") {
      nodes.push({
        kind: "rect",
        rect: decor.rect,
        fill: decor.fill,
        cornerRadius: decor.cornerRadius,
        opacity: decor.opacity,
      });
    } else {
      nodes.push({
        kind: "text",
        slot: "decor",
        text: decor.text,
        frame: decor.rect,
        fontFamily: decor.fontFamily,
        fontSize: decor.fontSize,
        fill: decor.fill,
        align: decor.align ?? "left",
        editable: false,
      });
    }
  }

  nodes.push(...overlayNodes);

  const viewersDef = template.textSlots.viewers;
  const viewersText = formatViewers(
    state.edits.viewers?.value ?? template.initialTexts.viewers,
  );
  nodes.push({
    kind: "text",
    slot: "viewers",
    text: viewersText,
    frame: viewersDef.frame,
    fontFamily: viewersDef.fontFamily,
    fontSize: fitFontSize(
      viewersText,
      viewersDef.fontSize,
      viewersDef.frame.width,
      viewersDef.fontFamily,
      measure,
    ),
    fill: viewersDef.fill,
    stroke: viewersDef.stroke,
    strokeWidth: viewersDef.strokeWidth,
    fontStyle: viewersDef.fontStyle,
    align: viewersDef.align,
    editable: true,
  });

  const telopDef = template.textSlots.telop;
  const telopText = (state.edits.telop?.text ?? template.initialTexts.telop).slice(
    0,
    telopDef.maxLength,
  );
  nodes.push({
    kind: "text",
    slot: "telop",
    text: telopText,
    frame: telopDef.frame,
    fontFamily: telopDef.fontFamily,
    fontSize: fitFontSize(
      telopText,
      telopDef.fontSize,
      telopDef.frame.width,
      telopDef.fontFamily,
      measure,
    ),
    fill: telopDef.fill,
    stroke: telopDef.stroke,
    strokeWidth: telopDef.strokeWidth,
    fontStyle: telopDef.fontStyle,
    align: telopDef.align,
    editable: true,
  });

  // 透かしは全出力に必須で、無効化できない（要件の受け入れ条件）。
  // 常に最後に積み、他要素に隠されないようにする
  const wm = template.watermark;
  const wmWidth = measure(wm.text, wm.fontSize, wm.fontFamily);
  nodes.push({
    kind: "text",
    slot: "watermark",
    text: wm.text,
    frame: {
      x: template.width - wmWidth - wm.paddingX,
      y: template.height - wm.fontSize - wm.paddingY,
      width: wmWidth + 4,
      height: wm.fontSize + 4,
    },
    fontFamily: wm.fontFamily,
    fontSize: wm.fontSize,
    fill: wm.fill,
    opacity: wm.opacity,
    align: "left",
    editable: false,
  });

  return nodes;
}
